// draftPredictor.js
// ---------------------------------------------------------------------------
// Predicts a real team's next draft move from historical pro drafts, purely
// from data (no heuristics / no guessing). Source data is the ygbuff
// "Draft Layout" tab → shared Supabase `pro_matches` table.
//
// Captains-Mode slot attribution (which of the 24 chronological slots belong
// to the first-pick (FP) vs second-pick (SP) team, and whether a slot is a
// pick or a ban) is ported verbatim from the authoritative implementation in
//   ygbuff/dota-scrim-viewer/server.js  (detectPatchStructure /
//   getPatchBanPositions, PICKS_FP / PICKS_SP).
// Keep these in sync if the upstream draft format ever changes.
// ---------------------------------------------------------------------------

import HeroDataModule from '../models/HeroData';

const { getHeroByName } = HeroDataModule;

// pro_matches columns for chronological draft positions 1..24.
export const ORDINAL_KEYS = [
  'pos_1st', 'pos_2nd', 'pos_3rd', 'pos_4th', 'pos_5th', 'pos_6th',
  'pos_7th', 'pos_8th', 'pos_9th', 'pos_10th', 'pos_11th', 'pos_12th',
  'pos_13th', 'pos_14th', 'pos_15th', 'pos_16th', 'pos_17th', 'pos_18th',
  'pos_19th', 'pos_20th', 'pos_21st', 'pos_22nd', 'pos_23rd', 'pos_24th',
];

// Picks are patch-independent. (Absolute chronological slot numbers, 1-based.)
export const PICKS_FP = [8, 14, 15, 18, 23];
export const PICKS_SP = [9, 13, 16, 17, 24];

// Ban slot ownership changed with the 7.4x ban reorder (cutoff Dec 16, 2025).
const PATCH_CUTOFF = new Date(2025, 11, 16); // local-time Dec 16 2025

export function detectPatchStructure(row) {
  const dateStr = (row && (row.date || row.Date)) || '';
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime()) && d >= PATCH_CUTOFF) return 'NEW';
  }
  return 'OLD';
}

export function getPatchBanPositions(patchStructure) {
  if (patchStructure === 'NEW') {
    return { BANS_FP: [1, 2, 5, 10, 11, 19, 21], BANS_SP: [3, 4, 6, 7, 12, 20, 22] };
  }
  return { BANS_FP: [1, 4, 7, 10, 11, 19, 22], BANS_SP: [2, 3, 5, 6, 12, 20, 21] };
}

// ── Hero-name normalization ────────────────────────────────────────────────
// pro_matches stores full display names that mostly line up with HeroData.name.
// A few historical spellings differ; map them onto the canonical roster name.
const NAME_ALIASES = {
  'outworld devourer': 'Outworld Destroyer',
  "nature prophet": "Nature's Prophet",
  'natures prophet': "Nature's Prophet",
  'anti mage': 'Anti-Mage',
  'queen of pain': 'Queen of Pain',
  'wraith king': 'Wraith King',
  'vengeful spirit': 'Vengeful Spirit',
  'shadow fiend': 'Shadow Fiend',
};

const _unresolvedLogged = new Set();

/** Canonical display name for a raw pro_matches hero string, or '' if blank. */
export function canonicalHeroName(raw) {
  // Normalize curly apostrophes (e.g. Nature's Prophet) to straight quotes.
  const name = (raw || '').replace(/[‘’ʼ]/g, "'").trim();
  if (!name) return '';
  const direct = getHeroByName(name);
  if (direct) return direct.name;
  const alias = NAME_ALIASES[name.toLowerCase()];
  if (alias) return alias;
  // Tolerate stray trailing punctuation from manual data entry (e.g. "Axe?").
  const cleaned = name.replace(/[^A-Za-z0-9'\-\s]+$/g, '').trim();
  if (cleaned && cleaned !== name) {
    const retry = getHeroByName(cleaned);
    if (retry) return retry.name;
  }
  // Last resort: keep the original string but flag it once in dev.
  if (process.env.NODE_ENV !== 'production' && !_unresolvedLogged.has(name)) {
    _unresolvedLogged.add(name);
    // eslint-disable-next-line no-console
    console.debug('[draftPredictor] unresolved hero name:', name);
  }
  return name;
}

const norm = (s) => (s || '').trim().toLowerCase();

// ── Per-match team attribution ─────────────────────────────────────────────

/**
 * Resolve, for one pro_matches row, which side the team is on and its draft.
 * @returns {null | { order:'fp'|'sp', won:boolean, opponent:string,
 *   pickSlots:number[], banSlots:number[], heroes:Set<string> /lowercased/,
 *   heroAt:(slot:number)=>string /canonical/ }}
 */
export function resolveTeamInMatch(row, teamName) {
  const t = norm(teamName);
  const isRadiant = norm(row.radiant_name) === t;
  const isDire = norm(row.dire_name) === t;
  if (!isRadiant && !isDire) return null;

  const order = norm(row.first_pick) === t ? 'fp' : 'sp';
  const { BANS_FP, BANS_SP } = getPatchBanPositions(detectPatchStructure(row));
  const pickSlots = (order === 'fp' ? PICKS_FP : PICKS_SP).slice().sort((a, b) => a - b);
  const banSlots = (order === 'fp' ? BANS_FP : BANS_SP).slice().sort((a, b) => a - b);

  const heroAt = (slot) => canonicalHeroName(row[ORDINAL_KEYS[slot - 1]]);

  // Full set of every hero present in the draft (both teams), for board overlap.
  const heroes = new Set();
  for (let s = 1; s <= 24; s++) {
    const h = canonicalHeroName(row[ORDINAL_KEYS[s - 1]]);
    if (h) heroes.add(h.toLowerCase());
  }

  const winner = norm(row.gamer_winner);
  return { order, won: winner === t, opponent: isRadiant ? row.dire_name : row.radiant_name,
    pickSlots, banSlots, heroes, heroAt };
}

/**
 * The ordered list of a team's own slots for the CURRENT (sim) patch, tagged
 * with the action ordinal within its type. Used to define "the next move".
 * @param {'fp'|'sp'} order
 * @param {'NEW'|'OLD'} patch  sim board patch (defaults to current = NEW)
 * @returns {{slot:number, type:'pick'|'ban', k:number}[]} sorted by slot
 */
export function teamSlots(order, patch = 'NEW') {
  const { BANS_FP, BANS_SP } = getPatchBanPositions(patch);
  const picks = (order === 'fp' ? PICKS_FP : PICKS_SP);
  const bans = (order === 'fp' ? BANS_FP : BANS_SP);
  const tagged = [
    ...picks.map((slot) => ({ slot, type: 'pick' })),
    ...bans.map((slot) => ({ slot, type: 'ban' })),
  ].sort((a, b) => a.slot - b.slot);
  // assign k = ordinal within type, in chronological order
  const counters = { pick: 0, ban: 0 };
  return tagged.map((e) => ({ ...e, k: ++counters[e.type] }));
}

/**
 * Read a team's k-th action of a given type from one match, using THAT match's
 * own patch-aware slot ownership. Patch-robust: ban ownership differs between
 * patches, so we index by action-ordinal, not absolute slot.
 */
function teamKthMove(resolved, type, k) {
  const slots = type === 'pick' ? resolved.pickSlots : resolved.banSlots;
  const slot = slots[k - 1];
  if (!slot) return '';
  return resolved.heroAt(slot);
}

/** The absolute 1-24 draft slot for a team's k-th action of a given type, in THIS match. */
function teamKthSlot(resolved, type, k) {
  const slots = type === 'pick' ? resolved.pickSlots : resolved.banSlots;
  return slots[k - 1] || null;
}

// ── Core prediction ────────────────────────────────────────────────────────

/**
 * Predict a team's next draft move (board-overlap, ranked) from history.
 *
 * @param {object} args
 * @param {string} args.team            team name (exact, from getProTeams)
 * @param {'fp'|'sp'} args.teamOrder    team's pick order in THIS simulation
 * @param {'pick'|'ban'} args.moveType  the type of the team's upcoming slot
 * @param {number} args.moveK           ordinal within that type (1-based)
 * @param {Set<string>|string[]} args.boardHeroes  hero names already on board
 * @param {Array} args.pool             the team's pro_matches rows
 * @param {number} [args.minOverlap=1]  overlap threshold for the "overlap" basis
 * @returns {null | { basis:'overlap'|'overall', poolSize:number, sampleSize:number,
 *   candidates: Array<{hero,count,total,pct,winRate,avgOverlap,
 *     sampleMatches:Array<{matchId,opponent,date,overlap,won,slot}>}> }}
 *   null ⇒ genuinely no data (team never drafted in this role / window).
 */
export function predictNextMove({ team, teamOrder, moveType, moveK, boardHeroes, pool, minOverlap = 1 }) {
  const boardSet = new Set(
    (boardHeroes instanceof Set ? Array.from(boardHeroes) : (boardHeroes || []))
      .map((h) => norm(typeof h === 'string' ? h : h && h.name))
      .filter(Boolean)
  );

  // Resolve every pool match for this team & keep only same pick-order games,
  // since the upcoming slot only "belongs" to the team in that role.
  const resolvedAll = [];
  for (const row of (pool || [])) {
    const r = resolveTeamInMatch(row, team);
    if (!r || r.order !== teamOrder) continue;
    const move = teamKthMove(r, moveType, moveK);
    if (!move) continue; // no hero recorded at that action in this match
    const slot = teamKthSlot(r, moveType, moveK);
    let overlap = 0;
    boardSet.forEach((h) => { if (r.heroes.has(h)) overlap += 1; });
    resolvedAll.push({ row, r, move, overlap, slot });
  }

  if (resolvedAll.length === 0) return null; // no data, no guessing

  // Prefer games that share heroes with the current board; fall back to the
  // team's unconditioned tendency for this slot when nothing overlaps yet.
  let basis = 'overlap';
  let considered = resolvedAll.filter((e) => e.overlap >= minOverlap);
  if (considered.length === 0) {
    basis = 'overall';
    considered = resolvedAll;
  }
  considered.sort((a, b) => b.overlap - a.overlap);

  // Aggregate the actual move across the considered games.
  const byHero = new Map();
  for (const e of considered) {
    const key = e.move;
    if (!byHero.has(key)) byHero.set(key, { hero: key, count: 0, wins: 0, overlapSum: 0, sampleMatches: [] });
    const agg = byHero.get(key);
    agg.count += 1;
    if (e.r.won) agg.wins += 1;
    agg.overlapSum += e.overlap;
    if (agg.sampleMatches.length < 10) {
      agg.sampleMatches.push({
        matchId: e.row.match_id, opponent: e.r.opponent, date: e.row.date,
        overlap: e.overlap, won: e.r.won, slot: e.slot,
      });
    }
  }

  const total = considered.length;
  const candidates = Array.from(byHero.values())
    .map((a) => ({
      hero: a.hero,
      count: a.count,
      total,
      pct: Math.round((a.count / total) * 100),
      winRate: Math.round((a.wins / a.count) * 100),
      avgOverlap: a.overlapSum / a.count,
      sampleMatches: a.sampleMatches.sort((x, y) => y.overlap - x.overlap),
    }))
    .sort((a, b) => (b.count - a.count) || (b.avgOverlap - a.avgOverlap));

  return { basis, poolSize: resolvedAll.length, sampleSize: total, candidates };
}
